/**
 * EpisodeSwitcher - Episode selection drawer for series/program content
 * Ported from Flutter TV's tv_episode_selector.dart
 */
import React, { useState, useEffect, useMemo, memo } from "react";
import {
  useFocusable,
  FocusContext,
} from "@noriginmedia/norigin-spatial-navigation";
import { useNavigate } from "react-router-dom";
import { getCMSImageURL } from "../../utils/api.utils";
import { useInputMode } from "../../utils/hooks/useInputMode";

/**
 * Back Button Component for drawer header
 */
var BackButton = memo(function BackButton(props) {
  var onClose = props.onClose;
  var focusKeyProp = props.focusKey || "episode-back-btn";

  var { focused, ref, focusSelf } = useFocusable({
    focusKey: focusKeyProp,
    onEnterPress: onClose,
  });

  // Input mode tracking for scroll behavior
  var inputMode = useInputMode();
  var isMouseFocus = inputMode.isMouseFocus;
  var setMouseFocus = inputMode.setMouseFocus;

  // Auto-scroll into view when focused (skip for mouse hover)
  useEffect(
    function () {
      if (focused && ref.current && !isMouseFocus) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    [focused, ref, isMouseFocus],
  );

  var handleMouseEnter = function () {
    setMouseFocus(true);
    if (focusSelf) focusSelf();
  };

  return React.createElement(
    "button",
    {
      ref: ref,
      className: "mnb-episode-back-btn " + (focused ? "focused" : ""),
      onClick: onClose,
      onMouseEnter: handleMouseEnter,
      type: "button",
    },
    React.createElement(
      "svg",
      {
        viewBox: "0 0 24 24",
        fill: "currentColor",
        width: 56,
        height: 56,
      },
      React.createElement("path", {
        d: "M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z",
      }),
    ),
  );
});

/**
 * Season Tab Component
 */
var SeasonTab = memo(function SeasonTab(props) {
  var season = props.season;
  var isSelected = props.isSelected;
  var isFocused = props.isFocused;
  var onSelect = props.onSelect;
  var focusKey = props.focusKey;

  var { ref, focused, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: onSelect,
  });

  // Input mode tracking for scroll behavior
  var inputMode = useInputMode();
  var isMouseFocus = inputMode.isMouseFocus;
  var setMouseFocus = inputMode.setMouseFocus;

  // Auto-scroll into view when focused (skip for mouse hover)
  useEffect(
    function () {
      if (focused && ref.current && !isMouseFocus) {
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
          inline: "center",
        });
      }
    },
    [focused, ref, isMouseFocus],
  );

  var handleMouseEnter = function () {
    setMouseFocus(true);
    if (focusSelf) focusSelf();
  };

  return React.createElement(
    "button",
    {
      ref: ref,
      className:
        "mnb-season-tab " +
        (isSelected ? "selected " : "") +
        (focused ? "focused" : ""),
      onClick: onSelect,
      onMouseEnter: handleMouseEnter,
      type: "button",
    },
    "فصل " + season,
  );
});

/**
 * Episode Card Component
 */
var EpisodeCard = memo(function EpisodeCard(props) {
  var episode = props.episode;
  var isCurrent = props.isCurrent;
  var isFocused = props.isFocused;
  var onSelect = props.onSelect;
  var focusKey = props.focusKey;

  var handleSelect = React.useCallback(
    function () {
      if (onSelect) onSelect(episode);
    },
    [onSelect, episode],
  );

  var { ref, focused, focusSelf } = useFocusable({
    focusKey: focusKey,
    onEnterPress: handleSelect,
  });

  // Input mode tracking for scroll behavior
  var inputMode = useInputMode();
  var isMouseFocus = inputMode.isMouseFocus;
  var setMouseFocus = inputMode.setMouseFocus;

  // Auto-scroll into view when focused (skip for mouse hover)
  useEffect(
    function () {
      if (focused && ref.current && !isMouseFocus) {
        ref.current.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }
    },
    [focused, ref, isMouseFocus],
  );

  var handleMouseEnter = function () {
    setMouseFocus(true);
    if (focusSelf) focusSelf();
  };

  // Extract image from contentPhotos if available, otherwise fallback
  var thumbnailUrl = useMemo(
    function () {
      if (!episode) return null;

      // 1. Try contentPhotos (Manabakh specific structure)
      if (
        episode.data?.meta?.contentPhotos &&
        Array.isArray(episode.data.meta.contentPhotos)
      ) {
        var photos = episode.data.meta.contentPhotos;
        var cover =
          photos.find((p) => p.type === "episodeCover" && p.device === "TV") ||
          photos.find((p) => p.type === "episodeCover" && p.device === "MD") ||
          photos.find((p) => p.type === "episodeCover");

        if (cover && (cover.src || cover.webp)) {
          return getCMSImageURL(cover.src || cover.webp);
        }
      }

      // 2. Fallbacks
      var path = episode.data?.poster || episode.poster || episode.img;
      return path ? getCMSImageURL(path) : null;
    },
    [episode],
  );

  var formatDuration = function (seconds) {
    if (!seconds) return "";
    var hours = Math.floor(seconds / 3600);
    var minutes = Math.floor((seconds % 3600) / 60);
    var secs = seconds % 60;

    if (hours > 0) {
      return (
        hours +
        ":" +
        String(minutes).padStart(2, "0") +
        ":" +
        String(secs).padStart(2, "0")
      );
    }
    return minutes + ":" + String(secs).padStart(2, "0");
  };

  return React.createElement(
    "div",
    {
      ref: ref,
      className:
        "mnb-episode-card " +
        (isCurrent ? "current " : "") +
        (focused ? "focused" : ""),
      onClick: handleSelect,
      onMouseEnter: handleMouseEnter,
    },
    // Thumbnail
    React.createElement(
      "div",
      { className: "mnb-episode-thumbnail" },
      thumbnailUrl
        ? React.createElement("img", {
            src: thumbnailUrl,
            alt: "قسمت " + episode.episodeNumber,
            loading: "lazy",
          })
        : React.createElement(
            "div",
            { className: "mnb-episode-thumbnail-placeholder" },
            React.createElement(
              "svg",
              {
                viewBox: "0 0 24 24",
                fill: "currentColor",
                width: 40,
                height: 40,
              },
              React.createElement("path", { d: "M8 5v14l11-7z" }),
            ),
          ),
    ),

    // Episode info
    React.createElement(
      "div",
      { className: "mnb-episode-info" },
      React.createElement(
        "div",
        { className: "mnb-episode-header" },
        React.createElement(
          "div",
          { className: "mnb-episode-number" },
          "قسمت " + episode.episodeNumber,
        ),
        isCurrent &&
          React.createElement(
            "div",
            { className: "mnb-episode-badge" },
            "در حال پخش",
          ),
      ),
      episode.data?.time &&
        React.createElement(
          "div",
          { className: "mnb-episode-duration" },
          React.createElement(
            "svg",
            {
              viewBox: "0 0 24 24",
              fill: "currentColor",
              width: 25,
              height: 25,
            },
            React.createElement("path", {
              d: "M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z",
            }),
          ),
          React.createElement("p", null, formatDuration(episode.data.time)),
        ),
    ),
  );
});

/**
 * Episode Selector Panel
 */
/**
 * Episode Selector Panel
 */
function EpisodeSelector(props) {
  var seriesInfo = props.seriesInfo;
  var currentSeason = props.currentSeason;
  var currentEpisode = props.currentEpisode;
  var contentSlug = props.contentSlug;
  var contentTitle = props.contentTitle;
  var onClose = props.onClose;

  var navigate = useNavigate();
  var [selectedSeason, setSelectedSeason] = useState(
    Number(currentSeason) || 1,
  );

  // Import setFocus for focus restoration
  var setFocus = require("@noriginmedia/norigin-spatial-navigation").setFocus;

  // Organize episodes by season
  var episodesBySeason = useMemo(
    function () {
      var organized = {};
      (seriesInfo || []).forEach(function (ep) {
        var season = ep.seasonNumber || 1;
        if (!organized[season]) {
          organized[season] = [];
        }
        organized[season].push(ep);
      });

      // Sort episodes by episode number within each season
      Object.keys(organized).forEach(function (season) {
        organized[season].sort(function (a, b) {
          return (a.episodeNumber || 0) - (b.episodeNumber || 0);
        });
      });

      return organized;
    },
    [seriesInfo],
  );

  var availableSeasons = useMemo(
    function () {
      return Object.keys(episodesBySeason)
        .map(Number)
        .sort(function (a, b) {
          return a - b;
        });
    },
    [episodesBySeason],
  );

  var currentSeasonEpisodes = episodesBySeason[selectedSeason] || [];

  // Focus Container
  var { ref, focusKey, focusSelf } = useFocusable({
    focusKey: "episodeSelector",
    trackChildren: true,
    isFocusBoundary: true,
    onArrowPress: function (direction) {
      // Close drawer on left arrow only if we are at the edge
      // But usually we want back button to handle close.
      // Let's rely on standard navigation.
      if (direction === "left") {
        // Check if we are in season tabs or episodes
        // If we want consistent closing behavior:
        // onClose();
        // return true;
        return true; // Stop propagation to prevent focusing underlay
      }
      return true;
    },
  });

  // Auto-focus logic
  useEffect(
    function () {
      // Try to focus current season or first season
      var seasonToFocus =
        "season-tab-" + availableSeasons.indexOf(selectedSeason);

      // Timer to ensure DOM is ready
      var timer = setTimeout(function () {
        // If current episode exists in current season, try to focus that
        var episodeIdx = currentSeasonEpisodes.findIndex(function (ep) {
          return ep.episodeNumber === Number(currentEpisode);
        });

        if (episodeIdx >= 0) {
          setFocus("episode-card-" + episodeIdx);
        } else {
          setFocus(seasonToFocus);
        }
      }, 100);

      return function () {
        clearTimeout(timer);
      };
    },
    [
      selectedSeason,
      currentEpisode,
      availableSeasons,
      currentSeasonEpisodes,
      setFocus,
    ],
  );

  var handleEpisodeSelect = React.useCallback(
    function (episode) {
      var newUrl =
        "/watch/" +
        contentSlug +
        "/" +
        selectedSeason +
        "/" +
        episode.episodeNumber;
      navigate(newUrl, { replace: true });
      onClose();
    },
    [contentSlug, selectedSeason, navigate, onClose],
  );

  return React.createElement(
    FocusContext.Provider,
    { value: focusKey },
    React.createElement(
      "div",
      { className: "mnb-episode-content", ref: ref },

      // Header
      React.createElement(
        "div",
        { className: "mnb-episode-header-bar" },
        React.createElement(
          "h2",
          { className: "mnb-episode-title" },
          contentTitle || "لیست قسمت‌ها",
        ),
        React.createElement(BackButton, { onClose: onClose }),
      ),

      // Season Tabs
      React.createElement(
        "div",
        { className: "mnb-season-tabs-container" },
        React.createElement(
          "div",
          { className: "mnb-season-tabs" },
          availableSeasons.map(function (season, index) {
            return React.createElement(SeasonTab, {
              key: "season-" + season,
              season: season,
              isSelected: season === selectedSeason,
              // isFocused prop removed, relying on internal hook
              onSelect: function () {
                setSelectedSeason(season);
              },
              focusKey: "season-tab-" + index,
            });
          }),
        ),
      ),

      React.createElement("div", { className: "mnb-episode-divider" }),

      // Episode Grid
      React.createElement(
        "div",
        { className: "mnb-episode-grid" },
        currentSeasonEpisodes.length > 0
          ? currentSeasonEpisodes.map(function (episode, index) {
              return React.createElement(EpisodeCard, {
                key: "episode-" + episode.episodeNumber,
                episode: episode,
                isCurrent:
                  selectedSeason === Number(currentSeason) &&
                  episode.episodeNumber === Number(currentEpisode),
                // isFocused prop removed
                onSelect: handleEpisodeSelect,
                focusKey: "episode-card-" + index,
              });
            })
          : React.createElement(
              "div",
              { className: "mnb-episode-empty" },
              "هیچ قسمتی در این فصل موجود نیست",
            ),
      ),
    ),
  );
}

/**
 * Main Episode Switcher Drawer
 */
var EpisodeSwitcher = function EpisodeSwitcher(props) {
  var isOpen = props.isOpen;
  var onClose = props.onClose;
  var seriesInfo = props.seriesInfo;
  var currentSeason = props.currentSeason;
  var currentEpisode = props.currentEpisode;
  var contentSlug = props.contentSlug;
  var contentTitle = props.contentTitle;
  var restoreFocusKey = props.restoreFocusKey;

  // Import setFocus for focus restoration
  var setFocus = require("@noriginmedia/norigin-spatial-navigation").setFocus;

  // Wrapper that closes drawer and restores focus
  var handleClose = function () {
    onClose();
    if (restoreFocusKey) {
      setTimeout(function () {
        setFocus(restoreFocusKey);
      }, 100);
    }
  };

  // Track if drawer should be visible (delayed state for animation)
  var [showDrawer, setShowDrawer] = useState(false);
  var [isAnimating, setIsAnimating] = useState(false);

  // Handle open/close with animation
  useEffect(
    function () {
      if (isOpen) {
        setShowDrawer(true);
        var timer = setTimeout(function () {
          setIsAnimating(true);
        }, 10);
        return function () {
          clearTimeout(timer);
        };
      } else {
        setIsAnimating(false);
        var timer = setTimeout(function () {
          setShowDrawer(false);
        }, 300);
        return function () {
          clearTimeout(timer);
        };
      }
    },
    [isOpen],
  );

  // Prevent scroll when open
  useEffect(
    function () {
      if (isOpen) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
      return function () {
        document.body.style.overflow = "";
      };
    },
    [isOpen],
  );

  // Handle keyboard events to close drawer
  useEffect(
    function () {
      if (!isOpen) return;

      var handleKeyDown = function (e) {
        if (
          e.key === "Escape" ||
          e.keyCode === 27 ||
          e.key === "Backspace" ||
          e.keyCode === 8 ||
          e.key === "XF86Back" ||
          e.keyCode === 10009 ||
          e.keyCode === 461
        ) {
          e.preventDefault();
          e.stopPropagation();
          handleClose();
        }
      };

      window.addEventListener("keydown", handleKeyDown, true);
      return function () {
        window.removeEventListener("keydown", handleKeyDown, true);
      };
    },
    [isOpen, handleClose],
  );

  if (!showDrawer || !seriesInfo || seriesInfo.length === 0) return null;

  return React.createElement(
    React.Fragment,
    null,
    // Backdrop
    React.createElement("div", {
      className: "mnb-episode-backdrop " + (isAnimating ? "visible" : ""),
      onClick: handleClose,
    }),

    // Drawer
    React.createElement(
      "div",
      { className: "mnb-episode-drawer " + (isAnimating ? "open" : "") },
      React.createElement(EpisodeSelector, {
        seriesInfo: seriesInfo,
        currentSeason: currentSeason,
        currentEpisode: currentEpisode,
        contentSlug: contentSlug,
        contentTitle: contentTitle,
        onClose: handleClose,
      }),
    ),
  );
};

export { EpisodeSwitcher };
